import {
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import {
  useAPI,
  updateRental,
  getLocationDoors,
  TLocationDoor,
  TOrder$TikiRental,
} from "../api.js";
import { t } from "i18next";
import { DialogAction } from "../components/DialogAction.js";
import { useAsyncValue } from "../useAsyncValue.js";
import { useCallback, useEffect, useState } from "react";
import { SuccessErrorToast } from "../components/SuccessErrorToast.js";

type Props = {
  rental: TOrder$TikiRental;
  tenantKey: string;
  locationID: string;
  doorID: string;
  openStatus: boolean;
  onClose: () => void;
};

export const RentalReassignDialog = ({
  rental,
  tenantKey,
  locationID,
  doorID,
  openStatus,
  onClose,
}: Props) => {
  const api = useAPI();
  const [locationDoors, setLocationDoors] = useState<TLocationDoor[]>([]);
  const [selectedDoorID, setSelectedDoorID] = useState<string>("");
  const rentalItem = rental.rentalItems.find((item) =>
    item.resources.find((resource) => resource.doorID === doorID),
  );

  const handleSubmit = useCallback(async () => {
    if (rentalItem && selectedDoorID) {
      setRentalReassignRequestPromise(
        updateRental(api, tenantKey, locationID, rental.rentalID, {
          itemId: rentalItem?.itemID,
          doorId: doorID,
          toDoorId: selectedDoorID,
        }),
      );
    }
  }, [api, locationID, tenantKey, rental, doorID, selectedDoorID]);

  const [rentalReassignRequestPromise, setRentalReassignRequestPromise] =
    useState<null | ReturnType<typeof updateRental>>(null);

  const rentalReassignRequestResult = useAsyncValue(
    rentalReassignRequestPromise,
  );

  useEffect(() => {
    const getLocationDoorData = async () => {
      const result = await getLocationDoors(api, tenantKey, locationID);
      setLocationDoors(result);
    };
    getLocationDoorData();
  }, [api, tenantKey, locationID]);

  useEffect(() => {
    if (rentalReassignRequestResult.type === "RESOLVED") {
      onClose();
      window.location.reload();
    }
  }, [rentalReassignRequestResult]);

  return (
    <>
      <SuccessErrorToast
        result={rentalReassignRequestResult}
        message={t("location_details.rental_reassign.success")}
      />
      <DialogAction
        status={openStatus}
        onClose={onClose}
        onClick={handleSubmit}
        translationKey="rental_reassign"
        disabled={rentalReassignRequestResult.type === "PENDING"}
      >
        <FormControl variant="outlined" fullWidth sx={{ margin: "10px" }}>
          <InputLabel id="rental-reassign-door-select">
            {t("location_details.rental_reassign.input_label")}
          </InputLabel>
          <Select
            labelId="rental-reassign-door-select-label"
            id="rental-reassign-door-select"
            value={selectedDoorID}
            onChange={(event: SelectChangeEvent) =>
              setSelectedDoorID(event.target.value)
            }
            label={t("location_details.rental_reassign.input_label")}
            placeholder={t("location_details.rental_reassign.input_label")}
          >
            {locationDoors
              .filter((door) => door.doorID !== doorID)
              .map((door: TLocationDoor) => (
                <MenuItem value={door.doorID} key={door.doorID}>
                  Door - {door.doorID}{" "}
                  <Chip
                    key={`${doorID}-chip`}
                    label={door.resourceTypes}
                    style={{ margin: "5px" }}
                  />
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </DialogAction>
    </>
  );
};
